import { Injectable, signal, computed } from '@angular/core';
import { NavLink } from '../section-renderers/section-renderer.models';

/**
 * Service to manage header state across components.
 * Allows app.component to detect if a Header section exists
 * and enables mobile menu state sharing.
 */
@Injectable({
  providedIn: 'root',
})
export class HeaderStateService {
  // Whether a Header section exists in the page
  private readonly _hasHeaderSection = signal<boolean>(false);

  // Mobile menu open state
  private readonly _isMobileMenuOpen = signal<boolean>(false);

  // Navigation links from header
  private readonly _navLinks = signal<NavLink[]>([]);

  // Logo URL or text for mobile menu header
  private readonly _logoUrl = signal<string | null>(null);
  private readonly _logoText = signal<string>('');

  // Public readonly signals
  readonly hasHeaderSection = this._hasHeaderSection.asReadonly();
  readonly isMobileMenuOpen = this._isMobileMenuOpen.asReadonly();
  readonly navLinks = this._navLinks.asReadonly();
  readonly logoUrl = this._logoUrl.asReadonly();
  readonly logoText = this._logoText.asReadonly();

  // Computed: display name for logo (text fallback if no URL)
  readonly logoDisplay = computed(() => this._logoUrl() || this._logoText() || 'Menu');

  /**
   * Register that a Header section exists (called by header-renderer)
   */
  registerHeader(navLinks: NavLink[], logoUrl?: string | null, logoText?: string): void {
    this._hasHeaderSection.set(true);
    this._navLinks.set(navLinks || []);
    this._logoUrl.set(logoUrl || null);
    this._logoText.set(logoText || '');
  }

  /**
   * Unregister header (called on destroy)
   */
  unregisterHeader(): void {
    this._hasHeaderSection.set(false);
    this._navLinks.set([]);
    this._logoUrl.set(null);
    this._logoText.set('');
    this._isMobileMenuOpen.set(false);
  }

  /**
   * Toggle mobile menu
   */
  toggleMobileMenu(): void {
    this._isMobileMenuOpen.update(v => !v);
  }

  /**
   * Close mobile menu
   */
  closeMobileMenu(): void {
    this._isMobileMenuOpen.set(false);
  }

  /**
   * Open mobile menu
   */
  openMobileMenu(): void {
    this._isMobileMenuOpen.set(true);
  }
}
